import React from 'react';
import {
    Box,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    TextField,
    Typography,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Checkbox,
    FormGroup,
    Stack,
    Paper,
    Alert,
} from '@mui/material';
import {
    ExpandMore,
    Person,
    LocationOn,
    Smartphone,
    VideoLibrary,
    NotificationsActive,
} from '@mui/icons-material';
import { useTranslate } from '@refinedev/core';
import {
    TeleSurveilleurConfig as TeleSurveilleurConfigType,
    MessageFormat,
    JsonConfig,
    SiaDc09Config,
    AlarmCodeMapping,
    USER_FIELDS,
    LOCATION_FIELDS,
    DEVICE_FIELDS,
    MEDIA_FIELDS,
    DEFAULT_JSON_CONFIG,
    DEFAULT_SIA_DC09_CONFIG,
    DEFAULT_ALARM_CODE_MAPPING,
    ALERT_TYPES,
    SIA_DC09_ALARM_CODES,
} from './types';
import { TemplateEditor } from './TemplateEditor';

interface TeleSurveilleurConfigProps {
    value: TeleSurveilleurConfigType;
    onChange: (config: TeleSurveilleurConfigType) => void;
}

export const TeleSurveilleurConfig: React.FC<TeleSurveilleurConfigProps> = ({
    value,
    onChange,
}) => {
    const translate = useTranslate();

    const handleFormatChange = (format: MessageFormat) => {
        const newConfig: TeleSurveilleurConfigType = {
            ...value,
            messageFormat: format,
        };

        // Set default config for the selected format
        if (format === 'JSON' && !value.jsonConfig) {
            newConfig.jsonConfig = DEFAULT_JSON_CONFIG;
        } else if (format === 'SIA_DC09' && !value.siaDc09Config) {
            newConfig.siaDc09Config = DEFAULT_SIA_DC09_CONFIG;
        } else if (format === 'PLAIN_TEXT' && !value.plainTextConfig) {
            newConfig.plainTextConfig = { template: '' };
        } else if (format === 'CUSTOM_TEMPLATE' && !value.customTemplateConfig) {
            newConfig.customTemplateConfig = { template: '', outputType: 'text' };
        }

        onChange(newConfig);
    };

    const handleJsonConfigChange = (updates: Partial<JsonConfig>) => {
        onChange({
            ...value,
            jsonConfig: {
                ...value.jsonConfig!,
                ...updates,
            },
        });
    };

    const handleJsonFieldsChange = (
        section: 'includeUser' | 'includeLocation' | 'includeDevice' | 'includeMedia',
        field: string,
        checked: boolean
    ) => {
        const currentFields = value.jsonConfig?.[section]?.fields || [];
        const newFields = checked
            ? [...currentFields, field]
            : currentFields.filter((f) => f !== field);

        handleJsonConfigChange({
            [section]: {
                ...value.jsonConfig?.[section],
                enabled: newFields.length > 0,
                fields: newFields,
            },
        });
    };

    const handleJsonSectionToggle = (
        section: 'includeUser' | 'includeLocation' | 'includeDevice' | 'includeMedia',
        enabled: boolean
    ) => {
        handleJsonConfigChange({
            [section]: {
                ...value.jsonConfig?.[section],
                enabled,
                fields: enabled ? value.jsonConfig?.[section]?.fields || [] : [],
            },
        });
    };

    const handleAlarmCodeChange = (alertType: keyof AlarmCodeMapping, code: string) => {
        onChange({
            ...value,
            alarmCodeMapping: {
                ...value.alarmCodeMapping,
                [alertType]: code,
            },
        });
    };

    const formatOptions: { value: MessageFormat; label: string; description: string }[] = [
        {
            value: 'JSON',
            label: 'JSON',
            description: 'Format structuré avec champs configurables',
        },
        {
            value: 'SIA_DC09',
            label: 'SIA DC-09',
            description: 'Protocole standard télésurveillance',
        },
        {
            value: 'PLAIN_TEXT',
            label: 'Texte brut',
            description: 'Message texte avec variables',
        },
        {
            value: 'CUSTOM_TEMPLATE',
            label: 'Template personnalisé',
            description: 'Format libre (JSON, XML, texte)',
        },
    ];

    return (
        <Box sx={{ mt: 2 }}>
            {/* Format selector */}
            <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>{translate('telesurveilleur.messageFormat', 'Format du message')}</InputLabel>
                <Select
                    value={value.messageFormat}
                    label={translate('telesurveilleur.messageFormat', 'Format du message')}
                    onChange={(e) => handleFormatChange(e.target.value as MessageFormat)}
                >
                    {formatOptions.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                            <Box>
                                <Typography variant="body1">{opt.label}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {opt.description}
                                </Typography>
                            </Box>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* JSON Configuration */}
            {value.messageFormat === 'JSON' && (
                <JsonConfigSection
                    config={value.jsonConfig || DEFAULT_JSON_CONFIG}
                    onConfigChange={handleJsonConfigChange}
                    onFieldChange={handleJsonFieldsChange}
                    onSectionToggle={handleJsonSectionToggle}
                />
            )}

            {/* SIA DC-09 Configuration */}
            {value.messageFormat === 'SIA_DC09' && (
                <SiaDc09ConfigSection
                    config={value.siaDc09Config || DEFAULT_SIA_DC09_CONFIG}
                    onChange={(siaDc09Config) => onChange({ ...value, siaDc09Config })}
                />
            )}

            {/* Plain Text Configuration */}
            {value.messageFormat === 'PLAIN_TEXT' && (
                <PlainTextConfigSection
                    config={value.plainTextConfig || { template: '' }}
                    onChange={(plainTextConfig) => onChange({ ...value, plainTextConfig })}
                />
            )}

            {/* Custom Template Configuration */}
            {value.messageFormat === 'CUSTOM_TEMPLATE' && (
                <CustomTemplateConfigSection
                    config={value.customTemplateConfig || { template: '', outputType: 'text' }}
                    onChange={(customTemplateConfig) => onChange({ ...value, customTemplateConfig })}
                />
            )}

            {/* Alarm Code Mapping - Always visible */}
            <AlarmCodeMappingSection
                config={value.alarmCodeMapping || DEFAULT_ALARM_CODE_MAPPING}
                onChange={handleAlarmCodeChange}
            />
        </Box>
    );
};

// JSON Config Section
interface JsonConfigSectionProps {
    config: JsonConfig;
    onConfigChange: (updates: Partial<JsonConfig>) => void;
    onFieldChange: (
        section: 'includeUser' | 'includeLocation' | 'includeDevice' | 'includeMedia',
        field: string,
        checked: boolean
    ) => void;
    onSectionToggle: (
        section: 'includeUser' | 'includeLocation' | 'includeDevice' | 'includeMedia',
        enabled: boolean
    ) => void;
}

const JsonConfigSection: React.FC<JsonConfigSectionProps> = ({
    config,
    onConfigChange,
    onFieldChange,
    onSectionToggle,
}) => {
    const translate = useTranslate();

    const sections = [
        {
            key: 'includeUser' as const,
            label: translate('telesurveilleur.json.user', 'Utilisateur'),
            icon: <Person />,
            fields: USER_FIELDS,
            fieldLabels: {
                internalId: 'ID interne',
                externalId: 'ID externe (PTI)',
                fullname: 'Nom complet',
                phone: 'Téléphone',
                email: 'Email',
            },
        },
        {
            key: 'includeLocation' as const,
            label: translate('telesurveilleur.json.location', 'Localisation'),
            icon: <LocationOn />,
            fields: LOCATION_FIELDS,
            fieldLabels: {
                latitude: 'Latitude',
                longitude: 'Longitude',
                timestamp: 'Horodatage',
                accuracy: 'Précision',
            },
        },
        {
            key: 'includeDevice' as const,
            label: translate('telesurveilleur.json.device', 'Appareil'),
            icon: <Smartphone />,
            fields: DEVICE_FIELDS,
            fieldLabels: {
                id: 'ID appareil',
                model: 'Modèle',
                os: 'Système',
                batteryLevel: 'Batterie',
            },
        },
        {
            key: 'includeMedia' as const,
            label: translate('telesurveilleur.json.media', 'Médias'),
            icon: <VideoLibrary />,
            fields: MEDIA_FIELDS,
            fieldLabels: {
                livestreamUrl: 'URL stream',
                recordedAudioUrl: 'URL audio',
            },
        },
    ];

    return (
        <Box>
            {/* Basic fields */}
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                    {translate('telesurveilleur.json.basicFields', 'Champs de base')}
                </Typography>
                <FormGroup row>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.includeAlertId}
                                onChange={(e) => onConfigChange({ includeAlertId: e.target.checked })}
                            />
                        }
                        label="ID Alerte"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.includeTimestamp}
                                onChange={(e) => onConfigChange({ includeTimestamp: e.target.checked })}
                            />
                        }
                        label="Horodatage"
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={config.includeAlertType}
                                onChange={(e) => onConfigChange({ includeAlertType: e.target.checked })}
                            />
                        }
                        label="Type d'alerte"
                    />
                </FormGroup>
            </Paper>

            {/* Expandable sections */}
            {sections.map((section) => (
                <Accordion key={section.key} defaultExpanded={config[section.key]?.enabled}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                            {section.icon}
                            <Typography sx={{ flexGrow: 1 }}>{section.label}</Typography>
                            <Switch
                                checked={config[section.key]?.enabled || false}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onSectionToggle(section.key, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                size="small"
                            />
                        </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                        <FormGroup>
                            {section.fields.map((field) => (
                                <FormControlLabel
                                    key={field}
                                    control={
                                        <Checkbox
                                            checked={config[section.key]?.fields?.includes(field) || false}
                                            onChange={(e) => onFieldChange(section.key, field, e.target.checked)}
                                            disabled={!config[section.key]?.enabled}
                                        />
                                    }
                                    label={section.fieldLabels[field as keyof typeof section.fieldLabels] || field}
                                />
                            ))}
                        </FormGroup>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};

// SIA DC-09 Config Section
interface SiaDc09ConfigSectionProps {
    config: SiaDc09Config;
    onChange: (config: SiaDc09Config) => void;
}

const SiaDc09ConfigSection: React.FC<SiaDc09ConfigSectionProps> = ({
    config,
    onChange,
}) => {
    const translate = useTranslate();

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
                {translate(
                    'telesurveilleur.sia.info',
                    'Le format SIA DC-09 est un standard de télésurveillance. Le message sera automatiquement formaté selon le protocole.'
                )}
            </Alert>

            <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <TextField
                        label={translate('telesurveilleur.sia.accountPrefix', 'Préfixe du compte')}
                        value={config.accountPrefix}
                        onChange={(e) => onChange({ ...config, accountPrefix: e.target.value })}
                        placeholder="L"
                        helperText="Ex: L pour ligne"
                        size="small"
                        sx={{ width: 150 }}
                    />

                    <TextField
                        label={translate('telesurveilleur.sia.receiverNumber', 'Numéro récepteur')}
                        value={config.receiverNumber}
                        onChange={(e) => onChange({ ...config, receiverNumber: e.target.value })}
                        placeholder="0"
                        size="small"
                        sx={{ width: 150 }}
                    />
                </Box>

                <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        {translate('telesurveilleur.sia.dataBlockOptions', 'Données à inclure dans le bloc')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                        {translate('telesurveilleur.sia.dataBlockHint', 'Le code alarme est automatiquement ajouté selon le mapping configuré ci-dessous')}
                    </Typography>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1 }}>
                        {/* Localisation */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeGps}
                                    onChange={(e) => onChange({ ...config, includeGps: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">📍 Position GPS</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeGpsTime}
                                    onChange={(e) => onChange({ ...config, includeGpsTime: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">🕐 Horodatage GPS</Typography>}
                        />

                        {/* Utilisateur */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeUserName}
                                    onChange={(e) => onChange({ ...config, includeUserName: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">👤 Nom utilisateur</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeUserId}
                                    onChange={(e) => onChange({ ...config, includeUserId: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">🆔 ID interne</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includePhone}
                                    onChange={(e) => onChange({ ...config, includePhone: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">📞 Téléphone</Typography>}
                        />

                        {/* Appareil */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeBatteryLevel}
                                    onChange={(e) => onChange({ ...config, includeBatteryLevel: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">🔋 Niveau batterie</Typography>}
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeDeviceInfo}
                                    onChange={(e) => onChange({ ...config, includeDeviceInfo: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">📱 Info appareil</Typography>}
                        />

                        {/* Media */}
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={config.includeLivestreamUrl}
                                    onChange={(e) => onChange({ ...config, includeLivestreamUrl: e.target.checked })}
                                />
                            }
                            label={<Typography variant="body2">🎥 URL stream vidéo</Typography>}
                        />
                    </Box>
                </Paper>
            </Stack>
        </Box>
    );
};

// Plain Text Config Section
interface PlainTextConfigSectionProps {
    config: { template: string };
    onChange: (config: { template: string }) => void;
}

const PlainTextConfigSection: React.FC<PlainTextConfigSectionProps> = ({
    config,
    onChange,
}) => {
    const translate = useTranslate();

    return (
        <Box>
            <TemplateEditor
                label={translate('telesurveilleur.plainText.template', 'Template du message')}
                value={config.template}
                onChange={(template) => onChange({ template })}
                rows={6}
            />
        </Box>
    );
};

// Custom Template Config Section
interface CustomTemplateConfigSectionProps {
    config: { template: string; outputType: 'text' | 'json' | 'xml' };
    onChange: (config: { template: string; outputType: 'text' | 'json' | 'xml' }) => void;
}

const CustomTemplateConfigSection: React.FC<CustomTemplateConfigSectionProps> = ({
    config,
    onChange,
}) => {
    const translate = useTranslate();

    return (
        <Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>{translate('telesurveilleur.custom.outputType', 'Type de sortie')}</InputLabel>
                <Select
                    value={config.outputType}
                    label={translate('telesurveilleur.custom.outputType', 'Type de sortie')}
                    onChange={(e) => onChange({ ...config, outputType: e.target.value as 'text' | 'json' | 'xml' })}
                >
                    <MenuItem value="text">Texte</MenuItem>
                    <MenuItem value="json">JSON</MenuItem>
                    <MenuItem value="xml">XML</MenuItem>
                </Select>
            </FormControl>

            <TemplateEditor
                label={translate('telesurveilleur.custom.template', 'Template personnalisé')}
                value={config.template}
                onChange={(template) => onChange({ ...config, template })}
                rows={8}
            />
        </Box>
    );
};

// Alarm Code Mapping Section
interface AlarmCodeMappingSectionProps {
    config: AlarmCodeMapping;
    onChange: (alertType: keyof AlarmCodeMapping, code: string) => void;
}

const AlarmCodeMappingSection: React.FC<AlarmCodeMappingSectionProps> = ({
    config,
    onChange,
}) => {
    const translate = useTranslate();

    return (
        <Accordion sx={{ mt: 3 }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <NotificationsActive color="warning" />
                    <Typography>
                        {translate('telesurveilleur.alarmCodes.title', 'Codes d\'alarme SIA DC-09')}
                    </Typography>
                </Stack>
            </AccordionSummary>
            <AccordionDetails>
                <Alert severity="info" sx={{ mb: 2 }}>
                    {translate(
                        'telesurveilleur.alarmCodes.info',
                        'Configurez le code SIA DC-09 à envoyer pour chaque type d\'alerte. Ces codes seront utilisés par le service d\'alerte pour communiquer avec le télésurveilleur.'
                    )}
                </Alert>

                <Stack spacing={2}>
                    {ALERT_TYPES.map((alertType) => (
                        <Box key={alertType.key} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography variant="body2" fontWeight="medium">
                                    {alertType.label}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {alertType.description}
                                </Typography>
                            </Box>
                            <FormControl size="small" sx={{ minWidth: 150 }}>
                                <Select
                                    value={config[alertType.key as keyof AlarmCodeMapping] || alertType.defaultCode}
                                    onChange={(e) => onChange(alertType.key as keyof AlarmCodeMapping, e.target.value)}
                                >
                                    {SIA_DC09_ALARM_CODES.map((code) => (
                                        <MenuItem key={code.code} value={code.code}>
                                            <Box>
                                                <Typography variant="body2" component="span" fontWeight="bold" fontFamily="monospace">
                                                    {code.code}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    {code.description.split(' - ')[0]}
                                                </Typography>
                                            </Box>
                                        </MenuItem>
                                    ))}
                                    <MenuItem value="">
                                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                            {translate('telesurveilleur.alarmCodes.custom', 'Code personnalisé...')}
                                        </Typography>
                                    </MenuItem>
                                </Select>
                            </FormControl>
                            {config[alertType.key as keyof AlarmCodeMapping] === '' && (
                                <TextField
                                    size="small"
                                    placeholder="Code"
                                    sx={{ width: 80 }}
                                    inputProps={{ maxLength: 5, style: { fontFamily: 'monospace' } }}
                                    onChange={(e) => onChange(alertType.key as keyof AlarmCodeMapping, e.target.value.toUpperCase())}
                                />
                            )}
                        </Box>
                    ))}
                </Stack>

                <Paper variant="outlined" sx={{ p: 1.5, mt: 2, bgcolor: 'grey.50' }}>
                    <Typography variant="caption" color="text.secondary">
                        <strong>Codes standards SIA DC-09 :</strong> NHA (Agression), NMA (Médical),
                        NTA (Chute), NPA (Panique), NNA (Inactivité), NLB (Batterie),
                        NZA (Zone entrée), NZR (Zone sortie), NRP (Fin d'alerte)
                    </Typography>
                </Paper>
            </AccordionDetails>
        </Accordion>
    );
};

export default TeleSurveilleurConfig;
